// Named, typed items for tactical-zone supply runs. Items are carried in a unit's
// bag inventory, banked to base storage on extract, lost on death, and can be
// equipped from storage between supply runs.
//
// Pure data + pure functions only: no state, no I/O, no randomness except via
// an injected `rng` (defaults to Math.random), so supply-run rolls and scattered
// placement stay unit-testable.

import { distanceInMeters } from "../road-graph.mjs";

export const ITEM_CATEGORIES = {
  valuables: "valuables", // no mechanical effect — pure banked value
  supplies: "supplies", // no mechanical effect — pure banked value
  food: "food", // the survival currency: banks to the base pantry (nutrition), NOT upgrade value
  materials: "materials", // base-building currency: banks to base materials, not the upgrade pool
  tools: "tools", // equipment candidates; effects apply only while equipped
};

export const ITEMS = {
  "instant-coffee": { id: "instant-coffee", name: "Instant Coffee", category: ITEM_CATEGORIES.valuables, value: 40, availableInSupplyRuns: false },
  "tampon-box": { id: "tampon-box", name: "Box of Tampons", category: ITEM_CATEGORIES.valuables, value: 60, availableInSupplyRuns: false },
  "mp3-player": { id: "mp3-player", name: "MP3 Player", category: ITEM_CATEGORIES.valuables, value: 50, availableInSupplyRuns: false },

  "scrap-metal": { id: "scrap-metal", name: "Scrap Metal", category: ITEM_CATEGORIES.materials, value: 0, materials: 1 },
  nails: { id: "nails", name: "Nails", category: ITEM_CATEGORIES.materials, value: 0, materials: 1 },
  screws: { id: "screws", name: "Wood Screws", category: ITEM_CATEGORIES.materials, value: 0, materials: 1 },
  "duct-tape": { id: "duct-tape", name: "Duct Tape", category: ITEM_CATEGORIES.materials, value: 0, materials: 1 },
  "first-aid-kit": {
    id: "first-aid-kit",
    name: "First Aid Kit",
    category: ITEM_CATEGORIES.supplies,
    value: 15,
    equipment: { slot: "kit", loadoutSlot: "defense", effects: { defenseBonus: 1 } },
  },
  "fuel-canister": { id: "fuel-canister", name: "Fuel Canister", category: ITEM_CATEGORIES.supplies, value: 15, availableInSupplyRuns: false },

  // Food: banks to the base pantry as `nutrition` units (the daily-quota
  // currency), not upgrade value — so a slot spent on food is a slot NOT spent on
  // value, which is the whole tension. `value: 0` keeps it out of the upgrade
  // pool. Dense staples like peanut butter or MRE cases carry more nutrition
  // than a single can.
  "canned-food": { id: "canned-food", name: "Canned Chili", category: ITEM_CATEGORIES.food, value: 0, nutrition: 1 },
  "bottled-water": { id: "bottled-water", name: "Water Jug", category: ITEM_CATEGORIES.food, value: 0, nutrition: 1 },
  "candy-bars": { id: "candy-bars", name: "Candy Bars", category: ITEM_CATEGORIES.food, value: 0, nutrition: 1 },
  "peanut-butter": { id: "peanut-butter", name: "Peanut Butter", category: ITEM_CATEGORIES.food, value: 0, nutrition: 2 },
  "mre-case": { id: "mre-case", name: "Case of MREs", category: ITEM_CATEGORIES.food, value: 0, nutrition: 2 },

  // Tools/equipment: passive only when equipped on a survivor between supply runs.
  crowbar: {
    id: "crowbar",
    name: "Crowbar",
    category: ITEM_CATEGORIES.tools,
    value: 20,
    equipment: { slot: "weapon", loadoutSlot: "weapon", effects: { offenseBonus: 1 } },
  },
  "baseball-bat": {
    id: "baseball-bat",
    name: "Baseball Bat",
    category: ITEM_CATEGORIES.tools,
    value: 20,
    equipment: { slot: "weapon", loadoutSlot: "weapon", effects: { offenseBonus: 1 } },
  },
  "lacrosse-stick": {
    id: "lacrosse-stick",
    name: "Lacrosse Stick",
    category: ITEM_CATEGORIES.tools,
    value: 25,
    equipment: { slot: "weapon", loadoutSlot: "weapon", effects: { offenseBonus: 1 } },
  },
  "kitchen-cleaver": {
    id: "kitchen-cleaver",
    name: "Kitchen Cleaver",
    category: ITEM_CATEGORIES.tools,
    value: 25,
    equipment: { slot: "weapon", loadoutSlot: "weapon", effects: { offenseBonus: 1 } },
  },
  "running-shoes": {
    id: "running-shoes",
    name: "Running Shoes",
    category: ITEM_CATEGORIES.tools,
    value: 20,
    equipment: { slot: "feet", loadoutSlot: "utility", effects: { moveBonusMetersPerTurn: 10 } },
  },
  backpack: {
    id: "backpack",
    name: "Backpack",
    category: ITEM_CATEGORIES.tools,
    value: 25,
    equipment: { slot: "pack", loadoutSlot: "utility", effects: { carryCapacityBonus: 1 } },
  },
  "bike-helmet": {
    id: "bike-helmet",
    name: "Bike Helmet",
    category: ITEM_CATEGORIES.tools,
    value: 15,
    equipment: { slot: "head", loadoutSlot: "defense", effects: { defenseBonus: 1 } },
  },
  "football-pads": {
    id: "football-pads",
    name: "Football Pads",
    category: ITEM_CATEGORIES.tools,
    value: 30,
    equipment: { slot: "torso", loadoutSlot: "defense", effects: { defenseBonus: 1 } },
  },
  "magazine-limb-armor": {
    id: "magazine-limb-armor",
    name: "Magazine Limb Armor",
    category: ITEM_CATEGORIES.tools,
    value: 15,
    equipment: { slot: "arms", loadoutSlot: "defense", effects: { defenseBonus: 1 } },
  },
  "work-gloves": {
    id: "work-gloves",
    name: "Work Gloves",
    category: ITEM_CATEGORIES.tools,
    value: 15,
    equipment: { slot: "hands", loadoutSlot: "utility", effects: { scavengeTurnsDelta: -1 } },
  },
  "steel-toe-boots": {
    id: "steel-toe-boots",
    name: "Steel-Toe Boots",
    category: ITEM_CATEGORIES.tools,
    value: 20,
    equipment: { slot: "feet", loadoutSlot: "defense", effects: { defenseBonus: 1 } },
  },
  flashlight: {
    id: "flashlight",
    name: "Flashlight",
    category: ITEM_CATEGORIES.tools,
    value: 15,
    equipment: { slot: "tool", loadoutSlot: "utility", effects: { scavengeTurnsDelta: -1 } },
  },
};

export function getItem(itemId) {
  return ITEMS[itemId] ?? null;
}

// Roll weights: food is the most common thing a supply run turns up (it has to be —
// the daily quota is the point), then supplies/materials, then the rarer tools.
// Valuables remain defined above, but are temporarily unavailable in supply-run rolls.
const ROLL_WEIGHTS = { food: 4, supplies: 3, materials: 3, tools: 1 };

const WEIGHTED_ITEM_POOL = Object.values(ITEMS).flatMap((item) =>
  item.availableInSupplyRuns === false ? [] : Array(ROLL_WEIGHTS[item.category] ?? 1).fill(item.id));

// Roll `count` item ids (with replacement — duplicates, e.g. two Canned
// Foods, are fine) from the weighted pool.
export function rollItems({ count = 1, rng = Math.random } = {}) {
  const items = [];
  for (let i = 0; i < count; i += 1) {
    items.push(WEIGHTED_ITEM_POOL[Math.floor(rng() * WEIGHTED_ITEM_POOL.length)]);
  }
  return items;
}

// What a POI scavenge yields: 1-3 items.
export function rollSearchItems({ rng = Math.random } = {}) {
  const count = 1 + Math.floor(rng() * 3);
  return rollItems({ count, rng });
}

// --- Potential-supplies preview (the building tooltip) ------------------------
// A building's contents are fixed and known internally, but the in-run tooltip
// only fog-reveals them: each slot shows "???" unless a stable per-slot roll
// lands in the "type" band (reveal its category) or the rarer "exact" band
// (reveal the specific item). The roll is seeded per building+slot so re-opening
// the tooltip never re-rolls; survivor skills will later WIDEN the bands (raise
// the chances), which — because the roll is fixed — strictly upgrades reveals
// and never regresses one. exact ⊂ "type or better", so P(see the type) is the
// two bands combined.
export const ITEM_PREVIEW_EXACT_CHANCE = 0.1; // see the exact item
export const ITEM_PREVIEW_TYPE_CHANCE = 0.5; // see (at least) the category

// The one-word "type" shown when only the category is revealed (e.g. "food").
export function itemCategoryLabel(category) {
  return category ?? "unknown";
}

// FNV-1a hash of a seed string → a stable float in [0, 1).
function unitFloatFromSeed(seed) {
  let hash = 2166136261;
  const str = String(seed);
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 4294967296;
}

// "exact" for the lowest rolls, then "type", then "hidden". Monotonic in the
// chances: widening a band (via skills) can only move a slot to a better tier.
export function itemRevealLevel(roll, exactChance, typeChance) {
  if (roll < exactChance) return "exact";
  if (roll < exactChance + typeChance) return "type";
  return "hidden";
}

// One entry per item in `contents`: { itemId, category, level } where level is
// "exact" | "type" | "hidden". `key` (the building's scavenge key) seeds the
// stable per-slot rolls. `exactChance`/`typeChance` are the reveal-band widths,
// left as params so a future survivor perk can raise them.
export function itemPreviewReveal({ key, contents, exactChance = ITEM_PREVIEW_EXACT_CHANCE, typeChance = ITEM_PREVIEW_TYPE_CHANCE } = {}) {
  return (contents ?? []).map((itemId, index) => {
    const item = getItem(itemId);
    const roll = unitFloatFromSeed(`item-preview:${key}:${index}`);
    return { itemId, category: item?.category ?? null, level: itemRevealLevel(roll, exactChance, typeChance) };
  });
}

// Compatibility helper for callers that still inspect carried bag effects.
// Bagged items no longer grant stats; equipment effects are aggregated in
// survivor-upgrades.mjs and baked into supply-run units at run start.
export function getCarriedEffects(inventory) {
  return { moveBonusMetersPerTurn: 0, scavengeTurnsDelta: 0 };
}

export function inventoryValue(inventory) {
  return (inventory || []).reduce((sum, itemId) => sum + (getItem(itemId)?.value ?? 0), 0);
}

export function isFood(itemId) {
  return getItem(itemId)?.category === ITEM_CATEGORIES.food;
}

export function isMaterial(itemId) {
  return getItem(itemId)?.category === ITEM_CATEGORIES.materials;
}

// Total nutrition (pantry food units) across an inventory — the survival
// counterpart to inventoryValue. Only food items carry nutrition.
export function inventoryNutrition(inventory) {
  return (inventory || []).reduce((sum, itemId) => sum + (getItem(itemId)?.nutrition ?? 0), 0);
}

// Total base-building materials across an inventory. Material-bearing supplies are
// carried out by units, then split into the base stockpile on extraction.
export function inventoryMaterials(inventory) {
  return (inventory || []).reduce((sum, itemId) => sum + (getItem(itemId)?.materials ?? 0), 0);
}

// Scatter standalone pickups around the zone's street corridors — real
// walkable points, not the POI itself — so exploring the block (not just
// beelining the objective) turns up supplies too. `minDistanceFromCenterMeters`
// keeps pickups off the POI's doorstep; `minSpacingMeters` keeps them from
// clustering on the same corner.
export function rollScatteredItems({
  zone,
  count = 3,
  rng = Math.random,
  minDistanceFromCenterMeters = 40,
  maxDistanceFromCenterMeters,
  minSpacingMeters = 50,
} = {}) {
  if (!zone?.corridors) return [];

  // Zone corridors keep their full geometry, which runs past the supply-run circle —
  // so cap pickups at the zone radius (the reachable play area), or they land
  // outside the circle where no unit can get to them.
  const maxDistance = Number.isFinite(maxDistanceFromCenterMeters) ? maxDistanceFromCenterMeters : (zone.radiusMeters ?? Infinity);

  const candidates = [];
  for (const corridor of zone.corridors.values()) {
    for (const coordinate of corridor.coordinates) {
      const distance = distanceInMeters(coordinate, zone.centerCoordinate);
      if (distance < minDistanceFromCenterMeters || distance > maxDistance) continue;
      candidates.push(coordinate);
    }
  }

  // Fisher-Yates with the injected rng, so placement is deterministic in tests.
  const shuffled = [...candidates];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  const chosen = [];
  for (const coordinate of shuffled) {
    if (chosen.length >= count) break;
    if (chosen.every((point) => distanceInMeters(point, coordinate) >= minSpacingMeters)) chosen.push(coordinate);
  }

  return chosen.map((position, index) => ({
    id: `pickup:${index + 1}`,
    itemId: rollItems({ count: 1, rng })[0],
    position: [...position],
  }));
}

// Post-run summary from a resolved tactical session's log: what every
// extracted unit banked vs. what every dead unit lost. Pure — reads only the
// `unit-extracted` / `unit-lost` log entries, which tactical-turn.mjs now
// stamps with the unit's final `inventory`.
export function summarizeTacticalOutcome(tacticalState) {
  const extracted = (tacticalState?.log ?? [])
    .filter((effect) => effect.type === "unit-extracted")
    .map((effect) => ({
      unitId: effect.unitId,
      survivorId: effect.survivorId,
      items: [...(effect.inventory ?? [])],
      value: inventoryValue(effect.inventory),
    }));

  const lost = (tacticalState?.log ?? [])
    .filter((effect) => effect.type === "unit-lost")
    .map((effect) => ({
      unitId: effect.unitId,
      survivorId: effect.survivorId,
      items: [...(effect.inventory ?? [])],
      value: inventoryValue(effect.inventory),
    }));

  return {
    extracted,
    lost,
    bankedValue: extracted.reduce((sum, entry) => sum + entry.value, 0),
    lostValue: lost.reduce((sum, entry) => sum + entry.value, 0),
  };
}
